import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Typography, IconButton } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
} from '../../components/base';
import { FORTA_BLUE, FORTA_WHITE } from '../../utils/colors';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';
import { Themes } from '../../constants';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IOption, IPayor, IPayorProviderCredential } from '../../utils/types';

const PayorProviderCredentialRow = ({
  payorProviderCredential,
  isAdding,
  isEditing,
  isDisabled,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}: {
  payorProviderCredential: IPayorProviderCredential;
  isAdding: boolean;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (editableCredential: IPayorProviderCredential) => void;
  onDelete: (editableCredential: IPayorProviderCredential) => void;
}) => {
  const { list: payorsList } = useSelector((state: RootState) => state.payors);
  const [editableCredential, setEditableCredential] = useState({ ...payorProviderCredential });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const handleFieldChange = (field: string, value: any) => {
    setEditableCredential((prevCredential: IPayorProviderCredential) => ({
      ...prevCredential,
      [field]: value,
    }));
  };

  const payorOptions = useMemo(
    () =>
      payorsList.map((payor: IPayor) => ({
        id: payor.id,
        label: `${payor?.label}, (${payor?.state?.toUpperCase()}, id ${payor?.id}, chpid ${
          payor?.clearinghouse_payor_id
        })`,
      })),
    [payorsList],
  );

  const validate = () => {
    const { payor, date_start } = editableCredential;
    if (!payor) {
      toast.error('Payor is required', { toastId: 'payor-error' });
      return false;
    }
    if (!date_start) {
      toast.error('Start date is required', { toastId: 'start-date-error' });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(editableCredential);
  };

  const handleCancel = () => {
    setEditableCredential({ ...payorProviderCredential });
    onCancel();
  };

  return (
    <Grid
      container
      spacing={1}
      sx={{
        display: 'flex',
        p: 1.5,
        mb: 2,
        backgroundColor: FORTA_WHITE,
        borderRadius: '10px',
        direction: 'row',
        alignItems: 'center',
      }}
    >
      <Grid item xs={2}>
        <Typography textAlign={'left'}>
          {`${payorProviderCredential?.provider?.first_name} ${payorProviderCredential?.provider?.last_name}`}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        {isAdding ? (
          <FHAutocomplete
            coloredBackground
            inputHeight={40}
            sx={{
              width: '100%',
              boxShadow: 'none',
              borderRadius: '8px',
            }}
            placeholder="Choose payor"
            options={payorOptions}
            renderOption={(props: any, option: IOption) => (
              <li {...props} key={option.id}>
                {option.label}
              </li>
            )}
            value={
              payorOptions.find((option: IOption) => option.id === editableCredential.payor?.id) ||
              ''
            }
            onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              handleFieldChange('payor', newValue ? newValue : '');
            }}
          />
        ) : (
          <Typography textAlign={'center'}>{`${
            payorProviderCredential.payor?.label
          }, (${payorProviderCredential.payor?.state?.toUpperCase()}, id ${
            payorProviderCredential.payor?.id
          }, chpid ${payorProviderCredential.payor?.clearinghouse_payor_id})`}</Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        {isAdding || isEditing ? (
          <FHAntdDateTimePicker
            coloredBackground
            format="MM/DD/YYYY"
            inputHeight={40}
            style={{ width: '100%', boxShadow: 'none' }}
            allowClear={false}
            value={editableCredential.date_start ? moment(editableCredential.date_start) : null}
            onSelect={(value: Moment) =>
              handleFieldChange('date_start', value.format('YYYY-MM-DD'))
            }
            onChange={(value: Moment) =>
              handleFieldChange('date_start', value.format('YYYY-MM-DD'))
            }
          />
        ) : (
          <Typography textAlign={'center'}>
            {payorProviderCredential.date_start
              ? moment(payorProviderCredential.date_start).format('MM/DD/YYYY')
              : 'N/A'}
          </Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        {isAdding || isEditing ? (
          <FHAntdDateTimePicker
            coloredBackground
            format="MM/DD/YYYY"
            inputHeight={40}
            style={{ width: '100%', boxShadow: 'none' }}
            allowClear={true}
            value={editableCredential.date_end ? moment(editableCredential.date_end) : null}
            onSelect={(value: Moment) => handleFieldChange('date_end', value.format('YYYY-MM-DD'))}
            onChange={(value: Moment | null) =>
              handleFieldChange('date_end', value ? value.format('YYYY-MM-DD') : null)
            }
          />
        ) : (
          <Typography textAlign={'center'}>
            {payorProviderCredential.date_end
              ? moment(payorProviderCredential.date_end).format('MM/DD/YYYY')
              : 'N/A'}
          </Typography>
        )}
      </Grid>

      <Grid item xs={1}>
        {isAdding || isEditing ? (
          <FHButton disabled={isDisabled} onClick={handleSave}>
            Save
          </FHButton>
        ) : (
          <IconButton
            sx={{
              width: 54,
              height: 30,
              border: `2px solid ${FORTA_BLUE.primary}`,
              borderRadius: '20px',
              backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
              mr: 3,
              '&:hover': {
                backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
              },
            }}
            onClick={onEdit}
          >
            <EditOutlinedIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
          </IconButton>
        )}
      </Grid>
      <Grid item xs={1}>
        {
          (isAdding || isEditing) && <FHButton onClick={handleCancel}>Cancel</FHButton>
          // : (
          // <IconButton
          //   sx={{
          //     width: 54,
          //     height: 30,
          //     border: `2px solid ${FORTA_BLUE.primary}`,
          //     borderRadius: '20px',
          //     backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
          //     mr: 3,
          //     '&:hover': {
          //       backgroundColor: isEditing ? FORTA_BLUE.primary : 'transparent',
          //     },
          //   }}
          //   onClick={() => setShowConfirmDialog(true)}
          // >
          //   <TrashIcon color={isEditing ? FORTA_WHITE : FORTA_BLUE.primary} width={18} />
          // </IconButton>
          // )
        }
      </Grid>
      <FHConfirmDialog
        open={showConfirmDialog}
        colorTheme={Themes.Primary}
        title={'Delete payor provider credential'}
        content={`Are you sure you want to delete this payor provider credential? This action cannot be undone.`}
        submittext={'Yes, Delete'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmDialog(false)}
        onSubmit={() => {
          setShowConfirmDialog(false);
          onDelete(editableCredential);
        }}
      />
    </Grid>
  );
};

export default PayorProviderCredentialRow;
